import { RefObject, useEffect, useRef, useState } from "react";

/**
 * How long the composer takes to glide from the centered layout down to where
 * the conversation docks it. The message is only handed over once the glide is
 * over, so the conversation mounts with its own composer exactly under the one
 * that just landed and the swap goes unnoticed.
 */
export const COMPOSER_GLIDE_DURATION_MS = 380;

type ComposerGlide = {
  /** Wrap the whole welcome screen: its bottom edge is where the glide ends. */
  rootRef: RefObject<HTMLDivElement | null>;
  /** Wrap the still-hidden disclaimer tray: its height is the landing gap. */
  disclaimerRef: RefObject<HTMLDivElement | null>;
  /** Whether the screen is on its way out. */
  isLeaving: boolean;
  /** Pixels the composer still has to travel, `null` until the glide starts. */
  distance: number | null;
  /** Starts the glide, then runs `onLanded` once it is over. */
  start: (onLanded: () => void) => void;
};

/**
 * Drives the new-conversation exit: the composer slides from the centered
 * welcome layout down to the spot the conversation docks it in, and the
 * caller's work is held back until it lands.
 */
export function useComposerGlide(): ComposerGlide {
  const rootRef = useRef<HTMLDivElement>(null);
  const disclaimerRef = useRef<HTMLDivElement>(null);
  const landingTimeoutRef = useRef<ReturnType<typeof setTimeout>>(null);
  const [distance, setDistance] = useState<number | null>(null);

  useEffect(
    () => () => {
      if (landingTimeoutRef.current) {
        clearTimeout(landingTimeoutRef.current);
      }
    },
    [],
  );

  // The conversation docks its composer against the bottom of this same area,
  // disclaimer tray included — so the travel is whatever room is left under
  // our own (still hidden) tray.
  const measureDistance = (): number => {
    const root = rootRef.current;
    const disclaimer = disclaimerRef.current;
    if (!root || !disclaimer) return 0;
    return (
      root.getBoundingClientRect().bottom -
      disclaimer.getBoundingClientRect().bottom
    );
  };

  const start = (onLanded: () => void) => {
    if (distance !== null) return;
    setDistance(measureDistance());
    landingTimeoutRef.current = setTimeout(
      onLanded,
      COMPOSER_GLIDE_DURATION_MS,
    );
  };

  return {
    rootRef,
    disclaimerRef,
    isLeaving: distance !== null,
    distance,
    start,
  };
}
